/**
 * "Items to Import" section of the process Items tab — new items that get
 * created when the process is approved (from `process.metadata`). Rows expand to
 * a detail grid (owner, status, price, warranty, tags, child components),
 * mirroring the legacy warehouse render.
 */
import { Chip } from '@vnetwork/ui/primitives';
import { DataTable, type ColumnDef } from '@vnetwork/ui/data';
import type { ImportItemMetadata } from '@/api/capacities';
import { formatWhCurrency, formatWhDate, generateRefPreview } from '../../shared/wh-format';

const muted = { color: 'var(--vn-fg-muted)' } as const;

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div style={{ ...muted, fontSize: 'var(--vn-font-size-xs)' }}>{label}</div>
      <div>{value ?? '-'}</div>
    </div>
  );
}

const columns: ColumnDef<ImportItemMetadata, unknown>[] = [
  {
    id: 'name',
    header: 'Name',
    cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name}</span>,
  },
  {
    id: 'category',
    header: 'Category',
    cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.category}</span>,
  },
  {
    id: 'type',
    header: 'Type',
    cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.item_type}</span>,
  },
  {
    id: 'qty',
    header: 'Qty/Tags',
    cell: ({ row }) => {
      const tags = row.original.service_tags ?? [];
      if (tags.length > 0) return <span>{tags.length} tags</span>;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
          <Chip tone="neutral">
            <span style={{ fontFamily: 'var(--vn-font-mono, monospace)' }}>
              {generateRefPreview(row.original.category, row.original.name, row.original.purchase_date)}
            </span>
          </Chip>
          {(row.original.quantity || 1) > 1 ? <Chip tone="neutral">×{row.original.quantity}</Chip> : null}
        </span>
      );
    },
  },
  {
    id: 'condition',
    header: 'Condition',
    cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.condition}</span>,
  },
];

export function ProcessImportItemsTable({ items }: { items: ImportItemMetadata[] }) {
  return (
    <DataTable
      columns={columns}
      data={items}
      getRowId={(_, i) => String(i)}
      enableSorting={false}
      getRowCanExpand={() => true}
      renderSubRow={(item) => {
        const tags = item.service_tags ?? [];
        return (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 'var(--vn-space-4)',
              fontSize: 'var(--vn-font-size-sm)',
              padding: 'var(--vn-space-4) var(--vn-space-6)',
            }}
          >
            <Field label="Owner" value={item.owner || '-'} />
            <Field label="Status" value={<span style={{ textTransform: 'capitalize' }}>{item.status || '-'}</span>} />
            <Field label="Purchase Price" value={formatWhCurrency(item.purchase_price)} />
            <Field label="Purchase Date" value={formatWhDate(item.purchase_date)} />
            <Field label="Warranty Start" value={formatWhDate(item.warranty_start)} />
            <Field label="Warranty End" value={formatWhDate(item.warranty_end)} />
            {item.description ? (
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Description" value={item.description} />
              </div>
            ) : null}
            {tags.length > 0 ? (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ ...muted, fontSize: 'var(--vn-font-size-xs)', marginBottom: 'var(--vn-space-1)' }}>
                  Service Tags / Serial Numbers
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
                  {tags.map((t, ti) => (
                    <Chip key={ti} tone="neutral">
                      {t}
                    </Chip>
                  ))}
                </div>
              </div>
            ) : null}
            {item.children && item.children.length > 0 ? (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ ...muted, fontSize: 'var(--vn-font-size-xs)', marginBottom: 'var(--vn-space-1)' }}>
                  Child Components
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
                  {item.children.map((c, ci) => (
                    <Chip key={ci} tone="neutral">
                      <span style={{ fontFamily: 'var(--vn-font-mono, monospace)' }}>
                        {c.name}
                        {(c.quantity || 1) > 1 ? ` ×${c.quantity}` : ''}
                      </span>
                    </Chip>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        );
      }}
    />
  );
}
